/**
 * Personal details shown across the site.
 *
 * Anything written as [[...]] is a placeholder: it renders on the site as a
 * clearly marked "Edit" chip, and placeholder links are never clickable.
 * Replace the whole string, brackets included, with your real details,
 * for example  name: 'Ada Lovelace'  or  github: 'https://github.com/ada'.
 */

export type ProfileLinkKey = 'github' | 'linkedin'

export type ExperienceItem = {
    period: string
    role: string
    organisation: string
    summary: string
}

export type SkillGroup = {
    title: string
    items: Array<string>
}

export const profile = {
    name: '[[Your name]]',
    role: 'Software engineer',
    location: '[[City, Country]]',
    email: '[[you@example.com]]',
    links: {
        github: '[[https://github.com/your-username]]',
        linkedin: '[[https://www.linkedin.com/in/your-profile]]'
    } satisfies Record<ProfileLinkKey, string>,
    introduction: [
        "I build calm, useful software, from self-hosted tools to autonomous agents. EDITAMA.TECH is my workspace in public: the projects I ship, the experiments I run in the AI Lab, and the notes I write along the way.",
        "[[Add two or three sentences about your background: where you work or study, what kind of problems you enjoy, and what brought you to software.]]"
    ],
    interests: [
        'Software engineering that stays maintainable after launch',
        'AI agents and the infrastructure that keeps them reliable',
        'Self-hosted tools on small, well-understood servers',
        'Workflow automation that removes busywork without hiding it',
        'Developer tools and writing that make hard things approachable'
    ],
    currentlyExploring: [
        'Running Hermes Agent on an Ubuntu server',
        'Telegram as an interface for AI workflows',
        'Memory systems for self-hosted AI agents',
        'Autonomous agents with clear, auditable boundaries'
    ],
    skills: [
        {
            title: 'Languages',
            items: ['[[e.g. TypeScript]]', '[[e.g. Python]]', '[[e.g. SQL]]']
        },
        {
            title: 'Web & applications',
            items: ['[[e.g. Next.js]]', '[[e.g. Node.js]]', '[[e.g. REST APIs]]']
        },
        {
            title: 'Infrastructure',
            items: ['[[e.g. Linux / Ubuntu Server]]', '[[e.g. Docker]]', '[[e.g. Nginx or Caddy]]']
        },
        {
            title: 'AI & automation',
            items: ['[[e.g. LLM APIs]]', '[[e.g. Agent frameworks]]', '[[e.g. Workflow tools]]']
        }
    ] satisfies Array<SkillGroup>,
    experience: [
        {
            period: '[[2024 to present]]',
            role: '[[Your role]]',
            organisation: '[[Organisation]]',
            summary: '[[One or two lines on what you built or were responsible for.]]'
        },
        {
            period: '[[2021 to 2024]]',
            role: '[[Your role]]',
            organisation: '[[Organisation]]',
            summary: '[[One or two lines on what you built or were responsible for.]]'
        }
    ] satisfies Array<ExperienceItem>
}

const PLACEHOLDER = /^\[\[([\s\S]*)\]\]$/

export const isPlaceholder = (value: string): boolean => PLACEHOLDER.test(value.trim())

/** The hint inside [[...]], or the value itself when it is real. */
export const placeholderText = (value: string): string => {
    const match = PLACEHOLDER.exec(value.trim())

    return match ? match[1] : value
}
